import { api } from './client';
import type { Actor, TagItem } from '../types/entities/actor.entity';

export interface Bot {
    id: number;
    key: string;
    name: string;
    slug: string;
    role: string | null;
    description: string | null;
    color: string | null;
    is_active: boolean;
    last_active_at: string | null;
    active_tokens_count?: number;
    created_at: string;
    updated_at: string;
}

export interface BotToken {
    id: string;
    name: string;
    abilities: string[];
    last_used_at: string | null;
    expires_at: string | null;
    created_at: string;
}

export interface BotPayload {
    name?: string;
    role?: string | null;
    description?: string | null;
    color?: string | null;
    is_active?: boolean;
}

export const actorService = {
    list: async (includeInactive = false): Promise<Actor[]> => {
        const response = await api.get<{ data: { actors: Actor[] } }>(`/actors${includeInactive ? '?include_inactive=1' : ''}`);
        return response.data.data.actors ?? [];
    },
    me: async (): Promise<Actor | null> => {
        const response = await api.get<{ data: { actor: Actor | null } }>('/actors/me');
        return response.data.data.actor;
    },
};

export const botService = {
    list: async (): Promise<Bot[]> => {
        const response = await api.get<{ data: { bots: Bot[] } }>('/bots');
        return response.data.data.bots ?? [];
    },
    create: async (payload: BotPayload & { name: string }): Promise<Bot> => {
        const response = await api.post<{ data: { bot: Bot } }>('/bots', payload);
        return response.data.data.bot;
    },
    update: async (id: number, payload: BotPayload): Promise<Bot> => {
        const response = await api.put<{ data: { bot: Bot } }>(`/bots/${id}`, payload);
        return response.data.data.bot;
    },
    deactivate: async (id: number): Promise<void> => {
        await api.delete(`/bots/${id}`);
    },
    tokens: async (id: number): Promise<BotToken[]> => {
        const response = await api.get<{ data: { tokens: BotToken[] } }>(`/bots/${id}/tokens`);
        return response.data.data.tokens ?? [];
    },
    createToken: async (id: number, payload: { name?: string; abilities?: string[]; expiration?: string | null }) => {
        const response = await api.post<{ data: { token: string; token_info: BotToken } }>(`/bots/${id}/tokens`, payload);
        return response.data.data;
    },
    revokeToken: async (id: number, tokenId: string): Promise<void> => {
        await api.delete(`/bots/${id}/tokens/${encodeURIComponent(tokenId)}`);
    },
};

export const tagService = {
    list: async (search?: string): Promise<TagItem[]> => {
        const suffix = search?.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
        const response = await api.get<{ data: { tags: TagItem[] } }>(`/tags${suffix}`);
        return response.data.data.tags ?? [];
    },
};
